import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * iPhoneでのシーク遅延を解消するためのWeb Audio APIフック
 * 音声ファイルを一度メモリ(AudioBuffer)に読み込むため、シークが爆速になります。
 * ※ 長時間の音声（10分以上など）には向きません。シャドーイング用の短い音声に最適です。
 */
export const useWebAudio = (url: string | null) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const audioContextRef = useRef<AudioContext | null>(null)
  const audioBufferRef = useRef<AudioBuffer | null>(null)
  const sourceNodeRef = useRef<AudioBufferSourceNode | null>(null)
  const startTimeRef = useRef(0) // 再生開始した AudioContext の時刻
  const startOffsetRef = useRef(0) // 再生開始した 音源内の位置(秒)
  const rafIdRef = useRef<number | null>(null) // アニメーションフレームID
  const currentTimeRef = useRef(0) // currentTimeのref版（依存配列の無限ループを防ぐ）

  // 1. ファイルをダウンロードしてデコードする
  useEffect(() => {
    if (!url) {
      setIsReady(false)
      setIsPlaying(false)
      setCurrentTime(0)
      currentTimeRef.current = 0
      return
    }

    setIsPlaying(false)
    setCurrentTime(0)
    currentTimeRef.current = 0
    startOffsetRef.current = 0
    startTimeRef.current = 0

    const loadAudio = async () => {
      try {
        setIsReady(false)
        setError(null)

        console.log('[useWebAudio] Starting audio load:', url)
        const isBlob = url.startsWith('blob:')
        const isDataURL = url.startsWith('data:')
        console.log('[useWebAudio] URL type:', { isBlob, isDataURL })

        // AudioContextの作成または取得
        const AudioContextClass =
          window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
        if (!audioContextRef.current && AudioContextClass) {
          audioContextRef.current = new AudioContextClass()
          console.log('[useWebAudio] Created AudioContext, state:', audioContextRef.current.state)

          // iOS対策: suspended状態の場合、即座にresume試行（ユーザー操作がある場合のみ成功）
          if (audioContextRef.current.state === 'suspended') {
            console.log('[useWebAudio] AudioContext is suspended, will resume on user interaction')
            // ここではresumeを試みず、play()時に実行する
          } else if (audioContextRef.current.state === 'running') {
            console.log('[useWebAudio] AudioContext is already running')
          }
        }

        if (!audioContextRef.current) {
          throw new Error('AudioContext is not supported')
        }

        console.log('[useWebAudio] Fetching audio data...')
        const response = await fetch(url)

        if (!response.ok) {
          throw new Error(`Fetch failed: ${response.status} ${response.statusText}`)
        }

        const contentType = response.headers.get('content-type')
        console.log('[useWebAudio] Response content-type:', contentType)

        const arrayBuffer = await response.arrayBuffer()
        console.log('[useWebAudio] ArrayBuffer size:', arrayBuffer.byteLength, 'bytes')

        if (arrayBuffer.byteLength === 0) {
          throw new Error('ArrayBuffer is empty')
        }

        // データをデコード（PCMデータに変換）
        console.log('[useWebAudio] Decoding audio data...')
        const decodedBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer)
        audioBufferRef.current = decodedBuffer
        setDuration(decodedBuffer.duration)
        setIsReady(true)
        console.log('[useWebAudio] Audio loaded successfully, duration:', decodedBuffer.duration, 'seconds')
      } catch (err) {
        console.error('[useWebAudio] Audio load failed:', err)
        console.error('[useWebAudio] URL:', url)
        setError(err instanceof Error ? err : new Error('Audio load failed'))
      }
    }

    loadAudio()

    return () => {
      console.log('[useWebAudio] cleaning up audio resources for:', url)
      // stop 相当をインライン展開（cleanup 中に setState を呼ばないため setIsPlaying は省略）
      const sourceNode = sourceNodeRef.current
      if (sourceNode) {
        sourceNode.onended = null
        try {
          sourceNode.stop()
        } catch (e) {
          console.log('[useWebAudio] error stopping source in cleanup:', e)
        }
        try {
          sourceNode.disconnect()
        } catch (e) {
          console.log('[useWebAudio] error disconnecting source in cleanup:', e)
        }
        sourceNodeRef.current = null
      }
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }
      if (audioContextRef.current) {
        console.log('[useWebAudio] closing AudioContext')
        audioContextRef.current.close()
        audioContextRef.current = null
      }
      if (audioBufferRef.current) {
        console.log('[useWebAudio] releasing AudioBuffer')
        audioBufferRef.current = null
      }
    }
  }, [url])

  // 停止処理
  const stop = () => {
    console.log('[useWebAudio] stop called')
    const sourceNode = sourceNodeRef.current
    if (sourceNode) {
      sourceNode.onended = null
      try {
        sourceNode.stop()
      } catch (e) {
        console.log('[useWebAudio] error stopping source:', e)
      }
      try {
        sourceNode.disconnect()
      } catch (e) {
        console.log('[useWebAudio] error disconnecting source:', e)
      }
      console.log('[useWebAudio] source node stopped and disconnected')
      sourceNodeRef.current = null
    }
    setIsPlaying(false)
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = null
    }
  }

  // 再生中の時間を更新するループ
  useEffect(() => {
    if (!isPlaying) {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }
      return
    }

    const loop = () => {
      if (!audioContextRef.current) return
      // 現在時刻 = (ACの現在時刻 - 再生開始時刻) + 再生開始時のオフセット
      const elapsed = audioContextRef.current.currentTime - startTimeRef.current
      const current = startOffsetRef.current + elapsed

      if (current >= duration) {
        // 最後まで到達したら時刻だけ更新して、停止処理はonendedに任せる
        setCurrentTime(duration)
        currentTimeRef.current = duration
      } else {
        setCurrentTime(current)
        currentTimeRef.current = current
      }
      rafIdRef.current = requestAnimationFrame(loop)
    }

    rafIdRef.current = requestAnimationFrame(loop)
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }
    }
  }, [isPlaying, duration])

  // 再生処理
  const play = useCallback(
    async (offset?: number) => {
      console.log('[useWebAudio] play called:', {
        offset,
        currentTime: currentTimeRef.current,
        duration,
        hasAudioContext: !!audioContextRef.current,
        hasAudioBuffer: !!audioBufferRef.current,
        hasSourceNode: !!sourceNodeRef.current,
      })

      if (!audioContextRef.current || !audioBufferRef.current) {
        console.log('[useWebAudio] play aborted: missing context or buffer')
        return
      }

      // AudioContextの状態確認とresume試行（iOS対策の最終防御ライン）
      if (audioContextRef.current.state === 'suspended') {
        console.log(
          '[useWebAudio] play(): AudioContext is suspended, attempting to resume NOW (user gesture context)...',
        )
        try {
          // 重要: このresumeはplayボタンのクリックイベント内で実行されるため、
          // iOS Safariでもユーザージェスチャーとして認識される
          await audioContextRef.current.resume()
          console.log('[useWebAudio] play(): Resume completed successfully, state:', audioContextRef.current.state)

          if (String(audioContextRef.current.state) !== 'running') {
            const errorMsg = `[useWebAudio] play(): AudioContext is still not running after resume, state: ${audioContextRef.current.state}`
            console.error(errorMsg)
            // iOS対策: HTML5にフォールバックさせるためエラーをthrow
            throw new Error(errorMsg)
          }
        } catch (err) {
          console.error('[useWebAudio] play(): Failed to resume AudioContext:', err)
          // iOS対策: HTML5にフォールバックさせるためエラーを再throw
          throw err
        }
      }

      if (String(audioContextRef.current.state) !== 'running') {
        const errorMsg = `[useWebAudio] play(): AudioContext is not in running state: ${audioContextRef.current.state}`
        console.error(errorMsg)
        // iOS対策: HTML5にフォールバックさせるためエラーをthrow
        throw new Error(errorMsg)
      }

      console.log('[useWebAudio] play(): AudioContext is ready, proceeding with playback')

      // 既に再生中なら止める（重複再生防止）
      // 注: ここではstop()を呼ばず、直接sourceを停止する（setIsPlaying(false)を避けるため）
      if (sourceNodeRef.current) {
        console.log('[useWebAudio] stopping existing source node')
        // onendedハンドラーを無効化（古いsourceのonendedが発火しないように）
        sourceNodeRef.current.onended = null
        try {
          sourceNodeRef.current.stop()
          sourceNodeRef.current.disconnect()
          console.log('[useWebAudio] old source node released')
        } catch (e) {
          console.log('[useWebAudio] error stopping source:', e)
        }
        // RAFループはキャンセルしない: isPlayingが既にtrueの場合、setIsPlaying(true)では
        // useEffectが再実行されずRAFが再起動されないため、ここで止めると永久に止まる。
        // RAFループはstartTimeRef/startOffsetRefの最新値を自動的に参照するので、
        // 新しいsource開始後も正しく動作する。
        sourceNodeRef.current = null
      }

      // 新しいSourceNodeを毎回作成（AudioBufferSourceNodeは使い捨て）
      const source = audioContextRef.current.createBufferSource()
      source.buffer = audioBufferRef.current
      source.connect(audioContextRef.current.destination)

      // offsetが指定されていればそこから、なければ現在の続きから
      // ただし、最後まで再生済み（currentTime >= duration）の場合は0から
      let startOffset: number
      if (offset !== undefined) {
        startOffset = offset
      } else if (currentTimeRef.current >= duration) {
        startOffset = 0
      } else {
        startOffset = currentTimeRef.current
      }

      console.log('[useWebAudio] starting playback:', {
        startOffset,
        bufferDuration: audioBufferRef.current.duration,
      })

      try {
        source.start(0, startOffset)
        console.log('[useWebAudio] source.start() succeeded')
      } catch (e) {
        console.error('[useWebAudio] source.start() failed:', e)
        return
      }

      sourceNodeRef.current = source
      startTimeRef.current = audioContextRef.current.currentTime
      startOffsetRef.current = startOffset

      setIsPlaying(true)

      // 再生終了時の処理
      source.onended = () => {
        console.log('[useWebAudio] source.onended fired:', {
          isSameSource: sourceNodeRef.current === source,
        })
        // sourceNodeが変わっていなければ（手動停止でなければ）停止処理
        if (sourceNodeRef.current === source) {
          setIsPlaying(false)
          setCurrentTime(duration)
          currentTimeRef.current = duration
          if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current)
          }
          sourceNodeRef.current = null
          console.log('[useWebAudio] playback ended, source node released')
        }
      }
    },
    [duration],
  )

  // 一時停止
  const pause = () => {
    stop()
    // currentTimeはupdateProgressで更新された最新の値が残っている
  }

  // シーク処理（ここが爆速になります）
  const seek = useCallback(
    (time: number) => {
      const newTime = Math.max(0, Math.min(time, duration))
      setCurrentTime(newTime)
      currentTimeRef.current = newTime

      if (isPlaying) {
        // 再生中なら、音も即座に移動して再生継続
        play(newTime)
      } else {
        // 停止中なら、内部的な開始位置だけ更新しておく
        startOffsetRef.current = newTime
      }
    },
    [duration, isPlaying, play],
  )

  // AudioContext初期化関数（iOS対策：ユーザー操作時に呼び出す）
  // 注意: この関数はユーザーのタッチイベント内で直接呼び出す必要がある
  const initAudioContext = useCallback(async () => {
    if (!audioContextRef.current) {
      console.log('[useWebAudio] initAudioContext: AudioContext not yet created')
      return false
    }

    if (audioContextRef.current.state === 'running') {
      console.log('[useWebAudio] initAudioContext: AudioContext already running')
      return true
    }

    if (audioContextRef.current.state === 'suspended') {
      console.log('[useWebAudio] initAudioContext: Attempting to resume...')
      try {
        // ユーザー操作のコンテキスト内でresumeを開始し、完了を待つ
        await audioContextRef.current.resume()
        const state = audioContextRef.current.state
        console.log('[useWebAudio] initAudioContext: Resume completed successfully, state:', state)
        if (String(state) !== 'running') {
          console.warn('[useWebAudio] initAudioContext: AudioContext state is not running after resume:', state)
          return false
        }
        return true
      } catch (err) {
        console.error('[useWebAudio] initAudioContext: Resume failed with error:', err)
        return false
      }
    }

    console.log('[useWebAudio] initAudioContext: Unknown state:', audioContextRef.current.state)
    return false
  }, [])

  return {
    isReady,
    isPlaying,
    duration,
    currentTime,
    error,
    play, // offset引数を受け取るplay関数を公開
    pause,
    seek, // これをスライダーのonChangeなどで呼ぶ
    stop,
    initAudioContext, // iOS対策用の初期化関数
  }
}
